import { TruckIcon } from '@heroicons/react/24/outline';

import { Badge } from '@/components/ui/catalyst/badge';
import { cn } from '@/lib/utils';
import type { CarrierTrackingData3 } from '@/services/api/generated/postgridService';

type BadgeColor =
  | 'zinc'
  | 'red'
  | 'orange'
  | 'amber'
  | 'yellow'
  | 'lime'
  | 'green'
  | 'emerald'
  | 'teal'
  | 'cyan'
  | 'sky'
  | 'blue'
  | 'indigo'
  | 'violet'
  | 'purple'
  | 'fuchsia'
  | 'pink'
  | 'rose';

const getCarrierStatusColor = (status: string | undefined): BadgeColor => {
  if (!status) return 'zinc';
  const statusLower = status.toLowerCase();
  if (statusLower.includes('delivered')) return 'green';
  if (statusLower.includes('transit') || statusLower.includes('out for'))
    return 'blue';
  if (statusLower.includes('failed') || statusLower.includes('exception'))
    return 'red';
  if (statusLower.includes('pending') || statusLower.includes('accepted'))
    return 'yellow';
  if (statusLower.includes('return')) return 'orange';
  return 'zinc';
};

const formatCarrierStatus = (status: string): string =>
  status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

interface CarrierTrackingBadgeProps {
  carrierTracking?: CarrierTrackingData3 | null;
  className?: string;
  size?: 'small' | 'medium';
}

const CarrierTrackingBadge = ({
  carrierTracking,
  className,
  size = 'small',
}: CarrierTrackingBadgeProps) => {
  if (!carrierTracking?.status) {
    return null;
  }

  const sizeClasses = {
    small: 'text-xs px-1.5 py-0.5',
    medium: 'text-sm px-2 py-1',
  };

  return (
    <Badge
      color={getCarrierStatusColor(carrierTracking.status)}
      className={cn(
        'inline-flex items-center gap-1 font-medium',
        sizeClasses[size],
        className,
      )}
    >
      <TruckIcon className="h-3 w-3" />
      {formatCarrierStatus(carrierTracking.status)}
    </Badge>
  );
};

export { CarrierTrackingBadge };
